import {
  actionCardShownProps,
  buildActionCardProps,
  getActionCardKind,
  type ActionCardAnalyticsCard,
  type ActionCardEventProps,
  type ActionCardFormFactor,
  type ActionCardSurface,
  type ActionCardAnalyticsContext,
  type DeferredDismissEvent,
  type DismissTarget,
} from '@littlebird/action-cards';

import { feedDensityStore } from '@/features/feed/feed-density.store';
import { trackEvent } from '@/utils/analytics';

import type { ActionCard } from '@littlebird/sdk';

export type ActionCardAction =
  | 'pin'
  | 'unpin'
  | 'snooze'
  | 'unsnooze'
  | 'restore'
  | 'delegate'
  | 'detach'
  | 'watch_keep';
// What started an action other than its own control: an Undo, or the Undo on the toast that says a card is done
export type ActionCardActionTrigger = 'undo' | 'done_toast';
export type ActionCardActionOutcome =
  | 'submitted'
  | 'queued'
  | 'succeeded'
  | 'failed';
export type ActionCardActionFailureStatus =
  | 'rejected'
  | 'changed'
  | 'refused'
  | 'error';

export type ActionCardCommand =
  | 'run'
  | 'acknowledge'
  | 'save_fields'
  | 'open_in_app'
  | 'start_chat'
  | 'dismiss'
  | 'complete_externally';

// The per-card+surface dedupe lives in the shared package so the event counts
// the same thing here and on mobile, whose lists remount far more often.
export const trackActionCardShown = (
  card: ActionCard,
  surface: ActionCardSurface,
  formFactor: ActionCardFormFactor,
  presentation?: Omit<ActionCardAnalyticsContext, 'surface' | 'retry'>
): void => {
  const context = actionCardAnalyticsContext(surface, {
    ...presentation,
    form_factor: formFactor,
  });
  const props = actionCardShownProps(card, context, formFactor);
  if (props)
    trackEvent('ACTION_CARD_SHOWN', {
      ...card.analytics,
      ...props,
    });
};

export const actionCardAnalyticsContext = (
  surface: ActionCardSurface,
  presentation: Omit<ActionCardAnalyticsContext, 'surface'> = {}
): ActionCardAnalyticsContext => {
  const density =
    surface === 'web_feeds' ? feedDensityStore.density : undefined;
  return {
    surface,
    ...(density ? { view_density: density } : {}),
    ...presentation,
  };
};

export const actionCardAnalyticsSnapshot = (
  card: ActionCardAnalyticsCard & Partial<Pick<ActionCard, 'analytics'>>,
  surface: ActionCardSurface | ActionCardAnalyticsContext,
  presentation: Omit<ActionCardAnalyticsContext, 'surface'> = {}
): ActionCardEventProps & Record<string, unknown> => {
  const context =
    typeof surface === 'string'
      ? actionCardAnalyticsContext(surface, presentation)
      : surface;
  return {
    ...(card.analytics ?? {}),
    ...buildActionCardProps(card, context),
  };
};

export type ActionCardAnalyticsSnapshot = ReturnType<
  typeof actionCardAnalyticsSnapshot
>;

export const trackCardAction = (
  action: ActionCardAction,
  props: ActionCardAnalyticsSnapshot,
  outcome: ActionCardActionOutcome,
  operationId: string,
  options?: {
    trigger?: ActionCardActionTrigger;
    outcomeStatus?: ActionCardActionFailureStatus;
  }
): void =>
  trackEvent('ACTION_CARD_ACTION', {
    ...props,
    action,
    outcome,
    operation_id: operationId,
    ...(options?.trigger ? { trigger: options.trigger } : {}),
    ...(options?.outcomeStatus
      ? { outcome_status: options.outcomeStatus }
      : {}),
  });

// A command the server never got to answer: its request failed, or the app could not queue it. The server records
// its own refusals and failed runs for every app
export const trackCardCommandFailed = (
  props: Record<string, unknown>,
  command: ActionCardCommand
): void => trackEvent('ACTION_CARD_COMMAND_FAILED', { ...props, command });

export const operationId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const targetProps = (target: DismissTarget, surface: ActionCardSurface) =>
  target.analytics ?? {
    card_id: target.cardId,
    action_type: target.actionType,
    card_kind: target.cardKind,
    provider_id: getActionCardKind(target.actionType)?.provider.id,
    source_app: undefined,
    origin: target.origin,
    status: target.status,
    surface: target.surface ?? surface,
  };

// Deferred dismissals commit from timers and unmounts inside the controller,
// so surfaces hand it this reporter instead of firing events at click time.
export const trackDismissal =
  (surface: ActionCardSurface) =>
  (event: DeferredDismissEvent): void => {
    for (const target of event.targets) {
      switch (event.kind) {
        case 'committed':
          trackEvent('ACTION_CARD_DISMISS_SUBMITTED', {
            ...targetProps(target, surface),
            reason: event.reason,
          });
          break;
        case 'undone':
          trackEvent(
            'ACTION_CARD_DISMISS_UNDONE',
            targetProps(target, surface)
          );
          break;
        case 'failed':
          trackCardCommandFailed(targetProps(target, surface), 'dismiss');
          break;
        default:
          event satisfies never;
      }
    }
  };
